import React, { useState } from 'react';
import { Phone, Mail, Send, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { FAQS, SITE } from '../data/mock';
import { submitMessage } from '../lib/api';

const GetHelp = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await submitMessage(form);
      toast.success('Message sent', { description: 'We typically respond within one working day.' });
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">SUPPORT</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Get help</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            Reach our coordinators in three ways. We answer in your language, and our toll-free line is open every working day.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <a href={`tel:${SITE.helpline}`} className="pillar-card bg-[#EDE3CC] rounded-2xl p-8 block cursor-pointer hover:bg-[#E4D8B8] transition-colors" aria-label={`Call our toll-free helpline ${SITE.helpline}`}>
            <Phone className="w-7 h-7 text-[#A36B1F] mb-4" strokeWidth={1.6} />
            <p className="text-xs tracking-[0.2em] text-[#5a5a5a] mb-2">TOLL-FREE</p>
            <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-2">{SITE.helpline}</h3>
            <p className="text-sm text-[#3a3a3a]">Mon – Sat, 9 AM – 8 PM IST · Tap to call</p>
          </a>
          <a href={`mailto:${SITE.email}?subject=Enquiry via LawForLayman website`} className="pillar-card bg-[#EDE3CC] rounded-2xl p-8 block cursor-pointer hover:bg-[#E4D8B8] transition-colors" aria-label={`Email us at ${SITE.email}`}>
            <Mail className="w-7 h-7 text-[#A36B1F] mb-4" strokeWidth={1.6} />
            <p className="text-xs tracking-[0.2em] text-[#5a5a5a] mb-2">EMAIL</p>
            <h3 className="font-serif-besley text-xl text-[#0F1B2D] mb-2 break-all">{SITE.email}</h3>
            <p className="text-sm text-[#3a3a3a]">Reply within one working day · Tap to compose</p>
          </a>
          <a
            href="#write-to-us"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById('write-to-us');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                const firstInput = el.querySelector('input, textarea');
                if (firstInput) setTimeout(() => firstInput.focus(), 500);
              }
            }}
            className="pillar-card bg-[#EDE3CC] rounded-2xl p-8 block cursor-pointer hover:bg-[#E4D8B8] transition-colors text-left w-full"
            aria-label="Open the message form below"
          >
            <Send className="w-7 h-7 text-[#A36B1F] mb-4" strokeWidth={1.6} />
            <p className="text-xs tracking-[0.2em] text-[#5a5a5a] mb-2">WRITE TO US</p>
            <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-2">Quick message</h3>
            <p className="text-sm text-[#3a3a3a]">Use the form below — anonymous if you prefer · Tap to open</p>
          </a>
        </div>
      </section>

      <section id="write-to-us" className="bg-[#EDE3CC] py-20 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-serif-besley text-4xl text-[#0F1B2D] mb-3">Send us a message</h2>
            <p className="text-[#3a3a3a] mb-8">Everything is confidential. A coordinator will respond personally.</p>
            <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-[#D6CAB0] rounded-2xl p-8">
              <div>
                <Label className="text-[#0F1B2D]">Your name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 border-[#E8DCC0]" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 border-[#E8DCC0]" />
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Message</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 border-[#E8DCC0]" />
              </div>
              <button type="submit" disabled={submitting} className="btn-orange inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium disabled:opacity-70">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Sending…' : 'Send message'}
              </button>
            </form>
          </div>

          <div>
            <h2 className="font-serif-besley text-4xl text-[#0F1B2D] mb-3">Frequently asked</h2>
            <p className="text-[#3a3a3a] mb-8">If you don&apos;t see your question here, write to us — we will add it.</p>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQS.map((f) => (
                <AccordionItem key={f.q} value={f.q} className="bg-white border border-[#D6CAB0] rounded-xl px-5">
                  <AccordionTrigger className="text-left font-medium text-[#0F1B2D] hover:no-underline py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] text-[#3a3a3a] leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetHelp;
